"use client";

import { useEffect, useState } from "react";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { ReviewCard } from "@/components/reviews/review-card";
import type { GoogleReview } from "@/lib/google-reviews";

type ReviewContent = {
  author_name?: string;
  author_image?: string;
  rating?: string;
  review_text?: string;
  review_date?: string;
  relative_time?: string;
  location?: string;
  source?: string;
};

type ReviewBlok = SbBlokData & ReviewContent;

type StoryblokInputEvent = { story?: { content?: ReviewContent } };
type StoryblokBridgeInstance = {
  on: (events: string | string[], cb: (payload?: StoryblokInputEvent) => void) => void;
};
type StoryblokBridgeWindow = Window & {
  StoryblokBridge?: new (opts?: object) => StoryblokBridgeInstance;
};

function toReview(c: ReviewContent): GoogleReview {
  return {
    authorName: c.author_name || "",
    authorImage: c.author_image || "",
    rating: Number(c.rating) || 5,
    relativeTimeDescription: c.relative_time || "",
    text: c.review_text || "",
    time: c.review_date ? new Date(c.review_date).getTime() / 1000 : 0,
    location: c.location || "",
    source: c.source || "Google",
  };
}

// Reviews are data stories used by ReviewsSection; this lets editors preview a
// single review card directly, with bridge `input` events keeping it in sync.
export default function Review({ blok }: { blok: ReviewBlok }) {
  const [content, setContent] = useState<ReviewContent>(blok);

  useEffect(() => {
    setContent(blok);
  }, [blok]);

  useEffect(() => {
    const w = window as StoryblokBridgeWindow;
    if (!w.StoryblokBridge) return;
    const bridge = new w.StoryblokBridge();
    bridge.on(["input", "change", "published"], (event) => {
      if (event?.story?.content) setContent(event.story.content);
    });
  }, []);

  return (
    <div {...storyblokEditable(blok)} className="bg-dusk-01 flex justify-center px-5 py-16">
      <div className="w-[285px]">
        <ReviewCard review={toReview(content)} />
      </div>
    </div>
  );
}
